import { format, differenceInDays } from 'date-fns';

export const getDateRange = (startDate, endDate, t) => {
    const toWord = t ? t("common.to") : "to";

    if (!startDate) {
        return t ? t("dateRange.invalid") : "Invalid date range";
    }

    if (!endDate) {
        return `${format(startDate, 'yyyy-MM-dd')} ${toWord} - - -`;
    }

    if (isSameDay(startDate, endDate)) {
        return `${format(startDate, 'yyyy-MM-dd')} `;
    }

    return `${format(startDate, 'yyyy-MM-dd')} ${toWord} ${format(endDate, 'yyyy-MM-dd')}`;
};

const FRIENDLY_DATE_RANGE_LABELS = {
    today: "Today: ",
    yesterday: "Yesterday: ",
    last7days: "Last 7 days: ",
    last2weeks: "Last 2 weeks: ",
    last30days: "Last 30 days: ",
    thisYear: "This year: ",
    prefix: "Date range: ",
};

const FRIENDLY_DATE_RANGE_I18N_KEYS = {
    today: "dateRange.todayPrefix",
    yesterday: "dateRange.yesterdayPrefix",
    last7days: "dateRange.last7daysPrefix",
    last2weeks: "dateRange.last2weeksPrefix",
    last30days: "dateRange.last30daysPrefix",
    thisYear: "dateRange.thisYearPrefix",
    prefix: "dateRange.prefix",
};

export const getFriendlyDateRangeKey = (startDate, endDate) => {
    if (!startDate || !endDate) {
        return "prefix";
    }

    const diffInDays = differenceInDays(endDate, startDate);
    const today = new Date();

    if (diffInDays === 0 && isSameDay(today, startDate)) {
        return "today";
    }

    if (isSameDay(startDate, endDate)) {
        return "prefix";
    }

    const now = new Date();
    const isCurrentYear = (start, end) => {
        const startOfYear = new Date(now.getFullYear(), 0, 1);
        return (
            start.toDateString() === startOfYear.toDateString() &&
            end.toDateString() === now.toDateString()
        );
    };

    if (diffInDays === 0 && isYesterday(startDate)) {
        return "yesterday";
    }
    if (diffInDays === 7) {
        return "last7days";
    }
    if (diffInDays === 13) {
        return "last2weeks";
    }
    if (diffInDays >= 28 && diffInDays <= 31) {
        return "last30days";
    }
    if (isCurrentYear(startDate, endDate)) {
        return "thisYear";
    }
    return "prefix";
};

export const getFriendlyDateRangeI18nKey = (startDate, endDate) =>
    FRIENDLY_DATE_RANGE_I18N_KEYS[getFriendlyDateRangeKey(startDate, endDate)];

export const getFriendlyDateRange = (startDate, endDate) =>
    FRIENDLY_DATE_RANGE_LABELS[getFriendlyDateRangeKey(startDate, endDate)];

// Helper function to check if two dates are the same day
export const isSameDay = (date1, date2) => {
    return (
        date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate()
    );
};

// Helper function to check if a date is yesterday
export const isYesterday = (date) => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return isSameDay(date, yesterday);
};

const parseSwedishDateInput = (val) => {
    if (!val) {
        return null;
    }

    const d = new Date(val);
    if (isNaN(d.getTime())) {
        return null;
    }

    return d;
};

export const formatSwedishDate = (val) => {
    const d = parseSwedishDateInput(val);
    if (!d) {
        return "�";
    }

    return d.toLocaleDateString("sv-SE", {
        timeZone: "Europe/Stockholm",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    });
};

export const formatSwedishClock = (val, includeSeconds = true) => {
    const d = parseSwedishDateInput(val);
    if (!d) {
        return "�";
    }

    const options = {
        timeZone: "Europe/Stockholm",
        hour: "2-digit",
        minute: "2-digit",
    };

    if (includeSeconds) {
        options.second = "2-digit";
    }

    return d.toLocaleTimeString("sv-SE", options);
};

export const formatSwedishTime = (val, includeSeconds = true) => {
    const d = parseSwedishDateInput(val);
    if (!d) {
        return "�";
    }

    const options = {
        timeZone: "Europe/Stockholm",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    };

    if (includeSeconds) {
        options.second = "2-digit";
    }

    return d.toLocaleString("sv-SE", options);
};

export const parseUtcDate = (val) => {
    if (!val) return new Date(0);
    if (typeof val === "string" && !val.endsWith("Z") && !val.match(/[+-]\d{2}:?\d{2}$/)) {
        return new Date(val + "Z");
    }
    const d = new Date(val);
    if (isNaN(d.getTime())) return new Date(0);
    return d;
};
export const asUtcIso =(dateString, endOfDay = false) => {
    const [year, month, day] = dateString.split("-").map(Number);
    const hour = endOfDay ? 23 : 0;
    const minute = endOfDay ? 59 : 0;
    const sec = endOfDay ? 0 : 0;

    return new Date(Date.UTC(year, month - 1, day, hour, minute, sec)).toISOString();
}

export function localTimeToUtcTimeString(timeHHmm, referenceDate) {
    if (!timeHHmm || !referenceDate) return null;

    const [hh, mm] = timeHHmm.split(":").map(Number);

    // anv�nd validFrom-datumet som referens
    const localDate = new Date(`${referenceDate}T00:00:00`);
    localDate.setHours(hh, mm, 0, 0);

    const utcH = String(localDate.getUTCHours()).padStart(2, "0");
    const utcM = String(localDate.getUTCMinutes()).padStart(2, "0");

    return `${utcH}:${utcM}`;
}

export function getUiDateLocale(language) {
    return (language || "en").startsWith("sv") ? "sv-SE" : "en-GB";
}

export function formatUiMonthYear(date, language) {
    if (!date) {
        return "";
    }

    const parsed = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(parsed.getTime())) {
        return "";
    }

    const formatted = parsed.toLocaleString(getUiDateLocale(language), {
        month: "long",
        year: "numeric",
    });
    if (!formatted) {
        return "";
    }

    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

export function utcTimeToLocalTimeString(utcHHmm, referenceDate) {
    console.log("converting utc to local:", utcHHmm, referenceDate);
    if (!utcHHmm || !referenceDate) return "";

    const [hh, mm] = utcHHmm.split(":").map(Number);

    // skapa UTC-datum
    const utcDate = new Date(`${referenceDate}T00:00:00Z`);
    utcDate.setUTCHours(hh, mm, 0, 0);

    // l�s ut lokal tid
    const localH = String(utcDate.getHours()).padStart(2, "0");
    const localM = String(utcDate.getMinutes()).padStart(2, "0");
    console.log("local time:", localH, localM);

    return `${localH}:${localM}`;
}